import { format, parseISO } from "date-fns";

import config from "../config/config";
import { KEY_homeImage, KEY_ViewFlag } from "../constants";
import { KeyValuePair, MeetingDetails, UserDetails } from "../types/interfaces";
import { RedirectType } from "../types/types";
import { getCurrentLanguage } from "./languageService";
import { LogVerbose } from "./logUtils";
import { readFromStorage, writeToStorage } from "./storageServies";

export { LogVerbose };

export const isString = (variable: unknown) => {
  return typeof variable === 'string' || variable instanceof String;
};

export const isNumeric = (str: number | string | null | undefined) => {
  if (!str)
    return false;
  if (typeof str === 'number')
    return true;
  const f = parseFloat(str);
  return !isNaN(f) && isFinite(f);
};

export const stringMatched = (str1: string, str2: string): boolean => {
  return str1.trim().toLowerCase() === str2.trim().toLowerCase();
};

export const dateStringMatched = (
  dateStr1: string,
  dateStr2: string,
): boolean => {
  try {
    const date1 = format(parseISO(dateStr1), 'yyyy-MM-dd');
    const date2 = format(parseISO(dateStr2), 'yyyy-MM-dd');
    return date1 === date2;
  } catch {
    return false;
  }
};

export const isValidEmailAddress = (email: string | null | undefined) => {
  if (!email) return false;
  return email.match(/^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*[!@$&]).{10,}$/);
};

export const isValidPhoneNumber = (phone: string | null | undefined) => {
  if (!phone) return false;
  return phone.match(/\(?\d{3}\)?[-. ]?\d{3}[-. ]?[-. ]?\d{4}/);
};

export const formatUsPhoneNumber = (phone: string): string => {
  // //https://learnersbucket.com/examples/javascript/how-to-format-phone-number-in-javascript/#google_vignette
  // const cleaned = phone.replace(/\D/g, '');
  // const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);

  // return match ? `(${match[1]}) ${match[2]}-${match[3]}` : phone;
  return formatPhoneNumber(phone, '(A) B-C');
};
export const formatPhoneNumber = (phone: string, pattern: string): string => {
  //https://learnersbucket.com/examples/javascript/how-to-format-phone-number-in-javascript/#google_vignette
  const cleaned = phone.replace(/\D/g, '');
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);

  if (!match) {
    return phone;
  }

  return pattern.replace('A', match[1]).replace('B', match[2]).replace('C', match[3]);
};

export const formatShortDateString = (dateStr: string | null | undefined) => {
  if (!dateStr) return undefined;

  const timeStamps = Date.parse(dateStr);
  if (Number.isNaN(timeStamps)) return dateStr;

  try {
    const date = new Date(timeStamps);
    return format(date, 'MM/dd/yyyy');
  } catch {
    return dateStr;
  }
};

// Re-exported from resultUtils.ts for backward compatibility (no store dependency)
export { FailedResult, ToFailedResult, OkResult, GeneralResult } from "./resultUtils";

//////////////////////////////////////////////////////////////////////////////////////////////
// A set of functions to check whether or not the current page is loaded within a mobile-app
// Based on the value of the query string parameter "platform"
// Caution: critical dependence on the query string parameter
//////////////////////////////////////////////////////////////////////////////////////////////
export const setViewFlag = () => {
  let platform = readFromStorage<string>(KEY_ViewFlag, 'LocalStorage');
  if (platform) return;

  const queryParams = new URL(window.location.href).searchParams;
  LogVerbose(`Query parameters: ${JSON.stringify(queryParams)}`);

  platform = queryParams.get('platform') || queryParams.get('platform');
  if (!platform) {
    // platform = 'browser';
    return;
  }

  writeToStorage(KEY_ViewFlag, platform.toLowerCase(), 'LocalStorage');
};

export const isIosApp = (): boolean => {
  const platform = readFromStorage<string>(KEY_ViewFlag, 'LocalStorage');
  const isIOS = platform != null && platform.toLowerCase() == 'ios';
  return isIOS;
};


//check if the page is loaded within an android WebView
//based on the flag "platform" stored in the LocalStorage
export const isAndroidApp = (): boolean => {
  const platform = readFromStorage<string>(KEY_ViewFlag, 'LocalStorage');
  const isAndroid = platform != null && platform.toLowerCase() == 'android';
  return isAndroid;
};
//###################################################################################################

//////////////////////////////////////////////////////////////////////////////////////////////
// A set of functions to check whether or not the current page is loaded within a mobile-app
// Based on the value of the user agent
// Warning: limited reliability
//////////////////////////////////////////////////////////////////////////////////////////////
export const isIosApp_UA = () => {
  //https://stackoverflow.com/questions/37591279/detect-if-user-is-using-webview-for-android-ios-or-a-regular-browser
  const userAgent = window.navigator.userAgent.toLowerCase();
  const ios = /iphone|ipod|ipad/.test(userAgent);
  if (!ios) {
    return false;
  }
  const safari = /safari/.test(userAgent);
  return !safari;
};

export const isAppleProduct_UA = () => {
  const userAgent = window.navigator.userAgent.toLowerCase();
  return /safari|iphone|ipod|ipad/.test(userAgent);
};

export const isAndroidApp_UA = () => {
  //https://webmasters.googleblog.com/2011/03/mo-better-to-also-detect-mobile-user.html
  const userAgent = window.navigator.userAgent.toLowerCase();
  const android = /android/.test(userAgent);
  if (!android) {
    return false;
  }
  const phone = /mobile/.test(userAgent);
  if (!phone) {
    return false;
  }
  const webview = /wv/.test(userAgent);
  return webview;
};
//##################################################################################################

export const isMobileApp = () => {
  // return true;
  if (config.appconfig.disableBrowserView === true) {
    console.log('Browser view is disabled by app configuration.');
    return true;
  }
  // LogVerbose('user-agent:', window.navigator.userAgent.toLowerCase());
  const isApp = isAndroidApp() || isIosApp();
  // LogVerbose('Is mobile App: ', isApp);
  return isApp;
};

export const getQueryParameter = (name: string): string | null => {
  const queryParams = new URL(window.location.href).searchParams;
  return queryParams.get(name);
};

export const getLanguageFromUrl = (url: string) => {
  try {
    //Note: if the url is a partial url, we will get an exception 
    const queryParams = new URL(url).searchParams;
    const languageQueryPara = queryParams?.get('lang') || queryParams?.get('language');
    if (!languageQueryPara)
      return undefined;
    return languageQueryPara.split('-')[0]?.trim().toLowerCase();
  }
  catch {
    // LogVerbose('Failed to extact Langauge query parameter due to ', e);
    // let langQP: string | undefined = undefined;
    const segs = url.split('?');
    if (segs.length > 1) {
      return segs[1]?.split('&').map((qps) => {
        const qp = qps.split('=');
        if (qp.length == 0) {
          return { name: undefined, value: undefined };
        }
        else if (qp.length == 1) {
          return { name: qp[0]?.trim().toLowerCase(), value: undefined };
        }
        else {
          return { name: qp[0]?.trim().toLowerCase(), value: qp[1]?.trim().toLowerCase() };
        }
      }).find(qpn => qpn.name == 'lang')?.value;
    }
    else {
      return undefined;
    }
  }
};


export const buildLocalizedUrl = (url: string, lang?: string) => {
  if (!url)
    return url;

  url = getUrlWithoutTrailingSlash(url)!;
  const languageQP = getLanguageFromUrl(url);
  if (languageQP)
    return url;

  // Get language from Redux store (single source of truth)
  const lan = lang || getCurrentLanguage();
  if (lan) {
    url = addQueryParameter(url, 'lang', lan);
  }
  return url;
};

export const isAbsoluteUrl = (url: string | null | undefined) => {
  return (url && (url.indexOf('://') > 0 || url.indexOf('//') === 0));
};

export const redirectToUrl = (url?: string, type?: RedirectType) => {
  if (!url)
    return;
  url = buildLocalizedUrl(url);
  if (!url) {
    console.error(`Url is undefined.`);
    return;
  }
  LogVerbose(`Redirecting to ${url} ...`);
  if (type === 'replace') {
    window.location.replace(url);
  } else if (type === 'new') {
    window.open(url, '_blank');
  } else {
    // window.location.href = url;
    window.open(url, '_self');
  }
};

export const getCookies = () => {
  const allCookies = decodeURIComponent(document.cookie)
    .split(';')
    .map((c) => {
      const np = c.split('=');
      return { name: np[0].trim(), value: np[1].trim() };
    });

  return allCookies;
};

export const getCookie = (name: string): string | undefined => {
  const n = name.trim().toLowerCase();
  const allCookies = getCookies();
  const cookie = allCookies.find((c) => c.name.toLowerCase() == n);
  return cookie?.value;
};

export const addQueryParameters = ({
  url,
  queryParameters,
}: {
  url: string;
  queryParameters: KeyValuePair<string>[];
}): string => {
  if (!url)
    return url;

  const queryStringSegs = queryParameters.filter(kvp => kvp.key && kvp.value).map(
    (kvp) => `${encodeURIComponent(kvp.key.trim())}=${encodeURIComponent(kvp.value.trim())}`,
  );
  const queryString = queryStringSegs.join('&');
  url = url.trim().replace(/\/$/, "");
  const joint = url.includes('?') ? '&' : '?';
  url = `${url}${joint}${queryString}`;
  return url;
};

export const addQueryParameter = (
  url: string,
  name: string,
  value: string,
): string => {
  if (!url || !name || !value)
    return url;
  url = url.trim().replace(/\/$/, "");
  const joint = url.includes('?') ? '&' : '?';
  const queryString = `${encodeURIComponent(name.trim())}=${encodeURIComponent(value.trim())}`;
  url = `${url}${joint}${queryString}`;
  return url;
};

// export const emailRegEx =
//   /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/;

// export const isValidEmailAddress = (email: string): boolean => {
//   return emailRegEx.test(email);
// };

export const isValidPattern = (pattern: string, regex: RegExp): boolean => {
  return regex.test(pattern);
};


export const getRandomImage = (images: string[]): string | undefined => {
  const cached = readFromStorage<string>(KEY_homeImage);
  if (cached) return cached;
  if (!images || images.length == 0) return undefined;
  const index = Math.floor(Math.random() * images.length);
  // As index is always a number, this is no risk of object injection
  // eslint-disable-next-line security/detect-object-injection    
  const headImage = images[index];
  writeToStorage(KEY_homeImage, headImage);
  return headImage;
};

export const getElementAttributes = ({ elementId, attributes }: { elementId: string; attributes: string[] }): KeyValuePair<string | undefined | null>[] | undefined => {
  if (!elementId || !attributes || attributes.length == 0) {
    return undefined;
  }
  const iframe = window.document.getElementById(elementId);
  if (!iframe) {
    LogVerbose(`Failed to get element with ID ${elementId}`);
    return undefined;
  }
  return attributes.map((name): KeyValuePair<string | undefined | null> => {
    const value = iframe.getAttribute(name);
    if (!value) {
      LogVerbose(`Failed to get attribute ${name} for element ${elementId}`);
    }
    return { key: name, value: value };
  });
};

export const getIframeHeightAttribute = (iframeId: string) => {
  const heightAttribute = getElementAttributes({ elementId: iframeId, attributes: ['height'] });
  if (!heightAttribute)
    return undefined;
  return heightAttribute[0].value;
};

export const getIframeContentHeight = (iframeId: string) => {
  const iframe = window.document.getElementById(iframeId);
  if (!iframe) {
    LogVerbose(`Failed to get element with ID ${iframeId}`);
    return undefined;
  }

  const iframeObj = iframe as HTMLObjectElement;
  try {
    const iframeDoc = iframeObj.contentDocument || iframeObj.contentWindow?.document;
    if (!iframeDoc)
      return undefined;
    return iframeDoc.body.scrollHeight;
  }
  catch (e) {
    LogVerbose(`failed to obtain iframe content height due to ${JSON.stringify(e)}`);
    return undefined;
  }

};

// LogVerbose is imported from logUtils.ts and re-exported above

export const sleep = (timeInMS: number) => {
  return new Promise((resolve) => setTimeout(resolve, timeInMS));
};


export const isTouchCapable = () => {
  return 'ontouchstart' in window;
};

export const getUrlWithoutTrailingSlash = (url?: string) => {
  return url?.replace(/\/$/, '');
};

export const getUrlWithoutqueryString = (url?: string) => {
  if (!url)
    return url;
  return getUrlWithoutTrailingSlash(url.split('?')[0]);
};

export const isPrintingSupported = () => {
  return window.print != undefined;
};

export const getCenterIndex = ({ nSlides, centerize }: { nSlides: number | undefined, centerize: boolean }): number | undefined => {
  if (!centerize || nSlides === undefined || nSlides <= 0) return undefined;
  const middleIndex = Math.floor(nSlides / 2);
  return nSlides % 2 === 0 ? middleIndex - 1 : middleIndex;
};

export const LocalizationTextProcessor = ({
  text,
  meetingDetails,
  userDetails,
  language
}: {
  text?: string,
  meetingDetails?: MeetingDetails,
  userDetails?: UserDetails,
  language?: string
}) => {
  if (!text) return '';
  //we need to sanitize the text
  const sanitize = (str: string) => str.replace(/</g, '&lt;').replace(/>/g, '&gt;');
  let processedText = sanitize(text);
  if (processedText.includes('${PROVIDER_NAME}')) {
    //replace {NAME} with actual provider name
    processedText = processedText.replace('${PROVIDER_NAME}', meetingDetails?.providerName ? `${meetingDetails.providerName}` : '');
  }

  if (processedText.includes('${TEL}')) {
    if (meetingDetails?.phoneNumber) {
      //replace {TEL} with actual phone number and insert tel: link in place of {TEL}
      const phoneNumber = meetingDetails.phoneNumber;
      // if (language === "ar") {
      //     //reverse phone number for Arabic
      //     phoneNumber = phoneNumber.split('').reverse().join('');
      // }
      processedText = processedText.replace('${TEL}', `<a href="tel:${meetingDetails.phoneNumber}">${phoneNumber}</a>`);
    }
    else {
      processedText = processedText.replace('${TEL}', '');
    }
  }

  if (processedText.includes('${MEETING_TIME}')) {
    //replace {MEETING_TIME} with actual meeting time
    const meetingTime = meetingDetails?.scheduledTime ? `${formatTime(meetingDetails.scheduledTime, language)}` : '';
    processedText = processedText.replace('${MEETING_TIME}', meetingTime);
  }

  if (processedText.includes('${PATIENT_NAME}')) {
    //replace {PATIENT_NAME} with actual patient name
    processedText = processedText.replace('${PATIENT_NAME}', userDetails?.firstName ? `${userDetails.firstName}` : '');
  }

  return processedText;
};


export const LocalizationHtmlProcessor = ({
  text,
  meetingDetails,
  userDetails,
  language
}: {
  text?: string,
  meetingDetails?: MeetingDetails,
  userDetails?: UserDetails,
  language?: string
}) => {
  if (!text) return '';
  //we need to sanitize the text
  const sanitize = (str: string) => str.replace(/</g, '&lt;').replace(/>/g, '&gt;');
  let processedText = sanitize(text);
  if (processedText.includes('${PROVIDER_NAME}')) {
    //replace {NAME} with actual provider name
    processedText = processedText.replace('${PROVIDER_NAME}',
      meetingDetails?.providerName ? `<span dir="ltr">${meetingDetails.providerName}</span>` : '');
  }

  if (processedText.includes('${TEL}')) {
    if (meetingDetails?.phoneNumber) {
      //replace {TEL} with actual phone number and insert tel: link in place of {TEL}
      const phoneNumber = meetingDetails.phoneNumber;
      processedText = processedText.replace('${TEL}', `<a href="tel:${meetingDetails.phoneNumber}"><span dir="ltr" class="phone-number">${phoneNumber}</span></a>`);
    }
    else {
      switch (language) {
        case 'en':
          processedText = processedText.replace('at ${TEL}', '');
          break;
        case 'es':
          processedText = processedText.replace('al ${TEL}', '');
          break;
        // Add more cases for other languages as needed
        default:
          processedText = processedText.replace('${TEL}', '');
      }
    }
  }

  if (processedText.includes('${MEETING_TIME}')) {
    //replace {MEETING_TIME} with actual meeting time
    const meetingTime = meetingDetails?.scheduledTime ? `<span dir="ltr">${formatTime(meetingDetails.scheduledTime, 'en')}</span>` : '';
    processedText = processedText.replace('${MEETING_TIME}', meetingTime);
  }

  if (processedText.includes('${PATIENT_NAME}')) {
    //replace {PATIENT_NAME} with actual patient name
    processedText = processedText.replace('${PATIENT_NAME}', userDetails?.firstName ? `<span dir="ltr">${userDetails.firstName}</span>` : '');
  }

  return processedText;
};

export const formatTime = (timeString: string, locale?: string) => {
  try {
    // If timeString is already a full date/time, use it directly
    let date = new Date(timeString);

    // If invalid, try combining with today's date
    if (isNaN(date.getTime())) {
      const today = new Date().toDateString();
      date = new Date(`${today} ${timeString}`);
    }

    // If still invalid, return original string
    if (isNaN(date.getTime())) {
      return timeString;
    }

    // Determine locale - fallback to 'en-US' if not provided
    const targetLocale = locale ? `${locale}-${getCountryCodeForLocale(locale)}` : 'en-US';

    const formattedTime = date.toLocaleTimeString(targetLocale, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: getHour12ForLocale(locale || 'en')
    });
    return formattedTime;
  } catch {
    return timeString;
  }
};

// Helper function to get country code for locale
const getCountryCodeForLocale = (locale: string): string => {
  const localeMap: { [key: string]: string } = {
    'en': 'US',
    'es': 'ES',
    'fr': 'FR',
    'de': 'DE',
    'it': 'IT',
    'pt': 'BR',
    'ar': 'SA',
    'zh': 'CN',
    'ja': 'JP',
    'ko': 'KR',
    'ru': 'RU',
    'vi': 'VN'
  };
  return localeMap[locale.toLowerCase()] || 'US';
};

// Helper function to determine if locale uses 12-hour format
const getHour12ForLocale = (locale: string): boolean => {
  const hour12Locales = ['en', 'ko']; // Languages that typically use 12-hour format
  return hour12Locales.includes(locale.toLowerCase());
};
